import styled from '@emotion/styled';
import Button from '@ep/components/Button';
import Container from '@ep/components/Container';
import { Label } from '@ep/components/Typography';
import theme from '@ep/theme';
import { Gcms_Post } from '@ep/types/gatsbyTypes';
import useScroll from '@ep/utils/useScroll';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import React, { useRef } from 'react';
import Post from './Post';

export interface PostCarouselModuleProps {
  label?: string;
  posts: Gcms_Post[];
  backgroundColor?: string;
}

const StyledContainer = styled(Container)`
  padding-bottom: 2rem;

  ${theme.mediaQueries.tablet} {
    padding: 3.75rem 0;
  }

  ${theme.mediaQueries.mobile} {
    padding: 2.5rem 0;
  }
`;

interface WrapperProps {
  themeColor?: string;
}

const Wrapper = styled.div<WrapperProps>`
  ${({ themeColor }) =>
    `
    color:${themeColor &&
      themeColor != theme.colors.white &&
      theme.colors.white};
  `}

  ${theme.mediaQueries.tablet} {
    margin: 0 auto;
    max-width: 28rem;
  }
`;

const StyledButton = styled(Button)`
  width: 8rem;
  height: 2.5rem;
  font-size: 1em;
  border: 1px solid ${theme.colors.oceanBlue};
  background-color: white;
  color: ${theme.colors.oceanBlue};
  :hover {
    background-color: ${theme.colors.oceanBlue}!important;
    border-color: ${theme.colors.oceanBlue}!important;
  }
`;

const StyledLabel = styled(Label)`
  text-align: center;
  margin-bottom: 2rem;

  ${theme.mediaQueries.tablet} {
    margin-bottom: 1rem;
  }

  ${theme.mediaQueries.mobile} {
    font-size: 1rem;
  }
`;

const PostDiv = styled.div`
  display: flex;
  position: relative;
  margin-bottom: 2.5rem;
  scroll-snap-type: x mandatory;
  overflow-x: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-bottom: 1rem;

  ::-webkit-scrollbar {
    display: none;
  }
`;

const PageIndicators = styled.div`
  display: flex;
  justify-content: center;
  clear: both;
`;

interface PageIndicatorProps {
  active: boolean;
  themeColor?: string;
}

const PageIndicator = styled.div<PageIndicatorProps>`
  margin: 1rem;
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background-color: transparent;
  transition: ${theme.transitions.create(['background-color', 'border-color'])};
  cursor: pointer;

  ${({ themeColor }) =>
    `
    border: 1px solid ${
      themeColor && themeColor != theme.colors.white
        ? theme.colors.white
        : theme.colors.mediumBlue
    };
  `}

  ${({ active, themeColor }) =>
    active
      ? `
        background-color: ${
          themeColor && themeColor != theme.colors.white
            ? theme.colors.white
            : theme.colors.mediumBlue
        };

      `
      : `
        background-color: ${themeColor}
      `}
`;

const NavContainer = styled.div`
  grid-column: 3;
  display: flex;
  justify-content: flex-end;
`;

const NavButton = styled(Button)`
  padding: 0;
  outline: none;
  border: none;
  background-color: rgba(0, 0, 0, 0);
  &:hover {
    background-color: rgba(0, 0, 0, 0);
  }
`;

const CarouselHeader = styled.div`
  display: grid;
  grid-template-columns: repeat(3 1fr);
`;

function PostCarouselModule({
  label,
  posts,
  backgroundColor,
}: PostCarouselModuleProps) {
  const postsRef = useRef<HTMLDivElement>(null);
  const { x } = useScroll(postsRef);
  const currentIndex = postsRef.current
    ? Math.round(x / postsRef.current.clientWidth)
    : 0;

  function scrollTo(index: number) {
    postsRef.current?.scroll({
      left: (postsRef.current.children[index] as HTMLElement).offsetLeft,
      behavior: 'smooth',
    });
  }

  const chunkPosts = (arr, size) =>
    arr.reduce(
      (acc, e, i) => (
        i % size ? acc[acc.length - 1].push(e) : acc.push([e]), acc
      ),
      [],
    );

  const postChunked: Gcms_Post[] = chunkPosts(posts, 3);

  const currentUrl =
    typeof window !== 'undefined' ? window.location.pathname : '';

  return (
    <StyledContainer backgroundColor={backgroundColor}>
      <Wrapper themeColor={backgroundColor}>
        <CarouselHeader>
          {currentUrl === '/newsroom/' && posts.length > 3 && (
            <StyledButton
              label="Show All"
              link={'/newsroom/posts/?type=' + posts[0].postType}
            />
          )}
          {currentUrl !== '/newsroom/' && <p></p>}
          <StyledLabel>
            <p>{label || 'Related Content'}</p>
          </StyledLabel>
          {posts.length > 3 && (
            <NavContainer>
              <NavButton
                onClick={() => scrollTo(currentIndex - 1)}
                color={`gray`}
                label={<ChevronLeftIcon fontSize="large" />}
                aria-label="related article left"
                disabled={currentIndex === 0}
              />
              <NavButton
                onClick={() => scrollTo(currentIndex + 1)}
                color={`gray`}
                label={<ChevronRightIcon fontSize="large" />}
                aria-label="related article left"
                disabled={currentIndex === postChunked.length - 1}
              />
            </NavContainer>
          )}
        </CarouselHeader>
        <PostDiv ref={postsRef} tabIndex={0}>
          {postChunked &&
            postChunked.map((post, index: number) => (
              <Post key={`chunkedPost__${index}`} post={post} />
            ))}
        </PostDiv>
      </Wrapper>
    </StyledContainer>
  );
}

export default React.memo(PostCarouselModule);
